import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Network } from "lucide-react";
import { consolidatedHoldings, holdingFichaPath } from "../../cobro/utils/holdings";
import { loadHoldingsWithConsolidation } from "../../cobro/utils/operatorHoldingsWithConsolidation";

// Holdings (de la lista del operador) en los que figura la empresa.
const holdingsOf = (holdings, companyId) =>
  holdings.filter((h) => (h.companies ?? []).some((c) => String(c?.id) === String(companyId)));

// Texto (clave i18n) por pestaña: `known` si billing dijo que el holding está encendido,
// `conditional` si no se pudo saber.
const TEXTS = {
  // Tarifas: la nota del holding usa la tarifa del holding; la del contrato solo cotiza en la
  // tienda (decisión 9 del 2026-09-25: el checkout no cambia).
  pricing: {
    known:
      "Esta empresa se cobra dentro del holding {{name}}, que tiene el cobro consolidado encendido: su nota usa la tarifa del holding, así que esta tarifa no se aplica a la nota de cobro. Solo se usa para cotizar envíos en su tienda.",
    conditional:
      "Esta empresa pertenece al holding {{name}}. Si su cobro consolidado está encendido, se cobra dentro de la nota del holding con la tarifa del holding, y esta tarifa solo se usa para cotizar envíos en su tienda.",
  },
  // Cobranza: la nota del holding vence con el plazo del holding (propio o el general) y va a
  // sus contactos de facturación; billing además la excluye de los recordatorios.
  collection: {
    known:
      "Esta empresa se cobra dentro del holding {{name}}, que tiene el cobro consolidado encendido: la nota del holding usa el plazo y los contactos de facturación del holding, así que el plazo, los recordatorios y los contactos de esta empresa no se aplican a esa nota.",
    conditional:
      "Esta empresa pertenece al holding {{name}}. Si su cobro consolidado está encendido, se cobra dentro de la nota del holding, que usa el plazo y los contactos de facturación del holding: el plazo, los recordatorios y los contactos de esta empresa no se aplican a esa nota.",
  },
};

/**
 * Aviso en la ficha de una empresa que pertenece a un holding con el cobro consolidado
 * ENCENDIDO: esa empresa se cobra dentro de la nota del holding, así que lo que se configura en
 * la pestaña no rige esa nota. `topic` elige la pestaña:
 *   · 'pricing' (Tarifas): la nota usa la tarifa del holding; la del contrato solo cotiza envíos
 *     en su tienda;
 *   · 'collection' (Cobranza): la nota usa el plazo y los contactos del holding; el plazo, los
 *     recordatorios y los contactos de la empresa no se aplican a ella.
 *
 * Informativo: si no se puede saber el estado del cobro consolidado, lo dice como condición; si
 * no se pueden leer los holdings, lo dice en una línea y la pestaña sigue funcionando.
 *
 * @param {{ companyId?: string|null, topic?: 'pricing'|'collection' }} props
 */
export default function HoldingMembershipNotice({ companyId, topic = "pricing" }) {
  const { t } = useTranslation();
  // { status: 'idle'|'ready'|'error', enabled: holding|null, member: holding|null, known }
  const [state, setState] = useState({ status: "idle" });

  useEffect(() => {
    if (!companyId) return undefined;
    let alive = true;
    setState({ status: "idle" });
    loadHoldingsWithConsolidation()
      .then(({ holdings, consolidationError }) => {
        if (!alive) return;
        const memberOf = holdingsOf(holdings, companyId);
        setState({
          status: "ready",
          known: !consolidationError,
          enabled: consolidatedHoldings(memberOf)[0] ?? null,
          member: memberOf[0] ?? null,
        });
      })
      .catch(() => {
        if (alive) setState({ status: "error" });
      });
    return () => {
      alive = false;
    };
  }, [companyId]);

  if (state.status === "error") {
    return (
      <p className="mb-4 text-xs text-fg-muted">
        {t("No se pudo comprobar si esta empresa se cobra dentro de un holding.")}
      </p>
    );
  }
  if (state.status !== "ready") return null;
  const holding = state.known ? state.enabled : state.member;
  if (!holding) return null;

  const name = holding.name || t("Sin nombre");
  const texts = TEXTS[topic] ?? TEXTS.pricing;
  return (
    <div
      role="note"
      className="mb-4 flex items-start gap-2 rounded-md border border-border bg-status-warning-soft p-3 text-sm text-status-warning"
    >
      <Network className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0 space-y-1">
        <p>{t(state.known ? texts.known : texts.conditional, { name })}</p>
        <a
          href={holdingFichaPath(holding.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-medium underline hover:no-underline"
        >
          {t("Abrir la ficha del holding")}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      </div>
    </div>
  );
}
